import { logError } from '@/utils/logger';
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { toast } from 'sonner';
import { MapContainer, TileLayer, CircleMarker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import html2canvas from 'html2canvas';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Plus, Trash, MagnifyingGlass, MapPin, ShareNetwork, DownloadSimple, NavigationArrow, PencilSimple } from '@phosphor-icons/react';
import SearchableSelect from '@/components/SearchableSelect';

const COLORS = ['#2563EB', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4', '#84CC16', '#F97316', '#6366F1'];

export default function SitesMap() {
  const [locations, setLocations] = useState([]);
  const [clients, setClients] = useState([]);
  const [userRole, setUserRole] = useState('');
  const [search, setSearch] = useState('');
  const [clientFilter, setClientFilter] = useState('all');
  const [satellite, setSatellite] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ client_id: '', site_name: '', latitude: '', longitude: '', notes: '' });
  const mapWrapRef = useRef(null);
  const API = process.env.REACT_APP_BACKEND_URL;

  const fetchAll = useCallback(async () => {
    try {
      const [locRes, cliRes, meRes] = await Promise.all([
        fetch(`${API}/api/site-locations`, { credentials: 'include' }),
        fetch(`${API}/api/clients`, { credentials: 'include' }),
        fetch(`${API}/api/auth/me`, { credentials: 'include' }),
      ]);
      if (locRes.ok) setLocations(await locRes.json());
      if (cliRes.ok) setClients(await cliRes.json());
      if (meRes.ok) setUserRole((await meRes.json()).role);
    } catch (error) { logError('Fetch locations:', error); }
  }, [API]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const canEdit = ['admin', 'supervisor', 'inspector', 'drh'].includes(userRole);

  const clientColor = useMemo(() => {
    const map = {};
    const ids = [...new Set(locations.map(l => l.client_id))];
    ids.forEach((id, i) => { map[id] = COLORS[i % COLORS.length]; });
    return map;
  }, [locations]);

  const filtered = locations.filter(l => {
    if (clientFilter !== 'all' && l.client_id !== clientFilter) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return l.site_name?.toLowerCase().includes(q) || l.client_name?.toLowerCase().includes(q);
  });

  const center = filtered.length > 0 ? [filtered[0].latitude, filtered[0].longitude] : [-4.325, 15.322];

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const url = editingId ? `${API}/api/site-locations/${editingId}` : `${API}/api/site-locations`;
      const res = await fetch(url, {
        method: editingId ? 'PUT' : 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (res.ok) {
        toast.success(editingId ? 'Localisation modifiée' : 'Site localisé sur la carte');
        setShowDialog(false);
        setEditingId(null);
        setForm({ client_id: '', site_name: '', latitude: '', longitude: '', notes: '' });
        fetchAll();
      } else { const err = await res.json(); toast.error(err.detail || 'Erreur'); }
    } catch { toast.error('Erreur'); }
  };

  const openEdit = (loc) => {
    setEditingId(loc.location_id);
    setForm({ client_id: loc.client_id || '', site_name: loc.site_name || '', latitude: String(loc.latitude), longitude: String(loc.longitude), notes: loc.notes || '' });
    setShowDialog(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette localisation ?')) return;
    try {
      const res = await fetch(`${API}/api/site-locations/${id}`, { method: 'DELETE', credentials: 'include' });
      if (res.ok) { toast.success('Supprimé'); fetchAll(); }
    } catch { toast.error('Erreur'); }
  };

  const shareMaps = (loc) => {
    const url = `https://www.google.com/maps?q=${loc.latitude},${loc.longitude}`;
    navigator.clipboard.writeText(url).then(() => toast.success('Lien Google Maps copié ! Partagez-le sur WhatsApp, SMS...'));
  };

  const downloadMap = async () => {
    if (!mapWrapRef.current) return;
    try {
      toast.info('Génération de la carte...');
      const canvas = await html2canvas(mapWrapRef.current, { useCORS: true, allowTaint: true, scale: 2 });
      const a = document.createElement('a');
      a.href = canvas.toDataURL('image/png');
      a.download = `carte-sites-gazelle-${new Date().toISOString().slice(0, 10)}.png`;
      a.click();
      toast.success('Carte téléchargée');
    } catch { toast.error('Erreur lors du téléchargement de la carte'); }
  };

  const useMyPosition = () => {
    if (!navigator.geolocation) { toast.error('Géolocalisation non disponible'); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => setForm(f => ({ ...f, latitude: pos.coords.latitude.toFixed(6), longitude: pos.coords.longitude.toFixed(6) })),
      () => toast.error('Impossible de récupérer votre position')
    );
  };

  const selectedClientSites = clients.find(c => c.client_id === form.client_id)?.sites || [];

  return (
    <div className="space-y-6" data-testid="sites-map-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#F8FAFC] tracking-tight">Carte des Sites</h1>
          <p className="text-[#94A3B8] mt-1 text-sm">Localisation GPS des sites, couleurs par client</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={downloadMap} variant="outline" className="border-[#334155] text-[#F8FAFC] hover:bg-[#1E293B]" data-testid="download-map-button">
            <DownloadSimple size={16} className="mr-2" /> Télécharger la carte
          </Button>
          {canEdit && (
            <Button onClick={() => setShowDialog(true)} className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white" data-testid="add-location-button">
              <Plus size={18} className="mr-2" /> Localiser un site
            </Button>
          )}
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher un site ou client..." className="bg-[#0F172A] border-[#1E293B] text-[#F8FAFC] pl-9" data-testid="search-location" />
        </div>
        <div className="sm:w-56">
          <SearchableSelect value={clientFilter} onValueChange={setClientFilter} options={[{ value: 'all', label: 'Tous les clients' }, ...clients.map(c => ({ value: c.client_id, label: c.name }))]} placeholder="Tous les clients" testId="map-client-filter" />
        </div>
      </div>

      {/* Légende clients */}
      {Object.keys(clientColor).length > 0 && (
        <div className="flex flex-wrap gap-2">
          {[...new Set(locations.map(l => l.client_id))].map(id => {
            const name = locations.find(l => l.client_id === id)?.client_name || '?';
            return (
              <Badge key={id} className="border text-xs" style={{ backgroundColor: `${clientColor[id]}20`, color: clientColor[id], borderColor: `${clientColor[id]}50` }}>
                ● {name}
              </Badge>
            );
          })}
        </div>
      )}

      <div ref={mapWrapRef} className="relative z-0 rounded-md overflow-hidden border border-[#1E293B]" data-testid="map-container">
        <button
          onClick={() => setSatellite(!satellite)}
          className="absolute top-3 right-3 z-[1000] px-3 py-2 rounded-md text-xs font-semibold shadow-lg transition-colors bg-[#0F172A]/90 text-[#F8FAFC] border border-[#334155] hover:bg-[#1E293B]"
          data-testid="satellite-toggle"
        >
          {satellite ? '🗺️ Vue Plan' : '🛰️ Vue Satellite'}
        </button>
        <MapContainer center={center} zoom={11} style={{ height: '480px', width: '100%' }} scrollWheelZoom>
          {satellite ? (
            <TileLayer key="sat" attribution='&copy; Esri' url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}" />
          ) : (
            <TileLayer key="osm" attribution='&copy; OpenStreetMap' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          )}
          {filtered.map(loc => (
            <CircleMarker key={loc.location_id} center={[loc.latitude, loc.longitude]} radius={10}
              pathOptions={{ color: clientColor[loc.client_id] || '#2563EB', fillColor: clientColor[loc.client_id] || '#2563EB', fillOpacity: 0.75 }}>
              <Popup>
                <div style={{ minWidth: 170 }}>
                  <strong>{loc.site_name}</strong><br />
                  <span style={{ color: '#64748B' }}>{loc.client_name}</span><br />
                  <span style={{ fontSize: 11 }}>{loc.latitude}, {loc.longitude}</span><br />
                  <a href={`https://www.google.com/maps?q=${loc.latitude},${loc.longitude}`} target="_blank" rel="noreferrer" style={{ color: '#2563EB' }}>Ouvrir dans Google Maps</a>
                </div>
              </Popup>
            </CircleMarker>
          ))}
        </MapContainer>
      </div>

      {/* Liste des sites localisés */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {filtered.map(loc => (
          <div key={loc.location_id} className="bg-[#0F172A] border border-[#1E293B] rounded-md p-4 flex items-start justify-between gap-2" data-testid={`location-${loc.location_id}`}>
            <div className="flex items-start gap-3 min-w-0">
              <MapPin size={20} weight="duotone" style={{ color: clientColor[loc.client_id] || '#2563EB' }} className="flex-shrink-0 mt-0.5" />
              <div className="min-w-0">
                <p className="text-sm font-medium text-[#F8FAFC] truncate">{loc.site_name}</p>
                <p className="text-xs text-[#94A3B8] truncate">{loc.client_name}</p>
                <p className="text-[10px] text-[#64748B] font-mono">{loc.latitude}, {loc.longitude}</p>
              </div>
            </div>
            <div className="flex flex-shrink-0">
              <Button size="sm" variant="ghost" onClick={() => shareMaps(loc)} className="text-[#10B981] hover:text-white hover:bg-[#10B981] h-8 w-8 p-0" title="Partager le lien Maps" data-testid={`share-location-${loc.location_id}`}>
                <ShareNetwork size={15} />
              </Button>
              {canEdit && (
                <Button size="sm" variant="ghost" onClick={() => openEdit(loc)} className="text-[#2563EB] hover:text-white hover:bg-[#2563EB] h-8 w-8 p-0" title="Modifier la localisation" data-testid={`edit-location-${loc.location_id}`}>
                  <PencilSimple size={15} />
                </Button>
              )}
              {userRole === 'admin' && (
                <Button size="sm" variant="ghost" onClick={() => handleDelete(loc.location_id)} className="text-[#EF4444] hover:text-white hover:bg-[#EF4444] h-8 w-8 p-0"><Trash size={15} /></Button>
              )}
            </div>
          </div>
        ))}
        {filtered.length === 0 && (
          <p className="text-[#94A3B8] text-sm col-span-3 text-center py-4">Aucun site localisé — cliquez sur « Localiser un site » pour commencer</p>
        )}
      </div>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-[#0F172A] border-[#1E293B] text-[#F8FAFC] max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-xl font-bold">{editingId ? 'Modifier la localisation' : 'Localiser un site'}</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="text-[#F8FAFC]">Client *</Label>
              <div className="mt-1">
                <SearchableSelect value={form.client_id} onValueChange={v => setForm({ ...form, client_id: v, site_name: '' })} options={clients.map(c => ({ value: c.client_id, label: c.name }))} placeholder="Choisir le client" testId="location-client-select" />
              </div>
            </div>
            <div>
              <Label className="text-[#F8FAFC]">Site *</Label>
              <div className="mt-1">
                {selectedClientSites.length > 0 ? (
                  <SearchableSelect value={form.site_name} onValueChange={v => setForm({ ...form, site_name: v })} options={selectedClientSites.map(s => ({ value: s, label: s }))} placeholder="Choisir le site" testId="location-site-select" />
                ) : (
                  <Input value={form.site_name} onChange={e => setForm({ ...form, site_name: e.target.value })} required placeholder="Nom du site" className="bg-[#1E293B] border-[#334155] text-[#F8FAFC]" data-testid="location-site-input" />
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[#F8FAFC]">Latitude *</Label>
                <Input value={form.latitude} onChange={e => setForm({ ...form, latitude: e.target.value })} required placeholder="-4.325000" className="bg-[#1E293B] border-[#334155] text-[#F8FAFC]" data-testid="location-lat" />
              </div>
              <div>
                <Label className="text-[#F8FAFC]">Longitude *</Label>
                <Input value={form.longitude} onChange={e => setForm({ ...form, longitude: e.target.value })} required placeholder="15.322000" className="bg-[#1E293B] border-[#334155] text-[#F8FAFC]" data-testid="location-lng" />
              </div>
            </div>
            <Button type="button" variant="outline" onClick={useMyPosition} className="w-full border-[#334155] text-[#94A3B8] hover:bg-[#1E293B]" data-testid="use-my-position-button">
              <NavigationArrow size={15} className="mr-2" /> Utiliser ma position actuelle (GPS)
            </Button>
            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)} className="border-[#334155] text-[#F8FAFC] hover:bg-[#1E293B]">Annuler</Button>
              <Button type="submit" className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white" data-testid="save-location-button">Enregistrer</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
